import React, { useState } from 'react';
import { X, Sparkles, ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ImageStyleSelect } from '../common/ImageStyleSelect';
import { ImageSizeSelect, ImageSize } from '../common/ImageSizeSelect';

interface AISimpleGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (imageStyle: string, imageSize: ImageSize) => void;
  onValidate: (imageUrl: string) => void;
  isLoading?: boolean;
  currentImageStyle?: string;
  currentImageSize?: ImageSize;
  generatedImageUrl?: string | null;
}

export const AISimpleGenerationModal: React.FC<AISimpleGenerationModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  onValidate,
  isLoading = false,
  currentImageStyle = 'auto',
  currentImageSize = 'square',
  generatedImageUrl = null
}) => {
  const { t } = useTranslation();
  const [imageStyle, setImageStyle] = useState<string>(currentImageStyle);
  const [imageSize, setImageSize] = useState<ImageSize>(currentImageSize);
  const [showFullSizePreview, setShowFullSizePreview] = useState(false);

  const handleGenerate = () => {
    onGenerate(imageStyle, imageSize);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Background overlay */}
      <div 
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg border-2 border-black shadow-xl max-w-xl w-full p-6">
          {/* Close button */}
          <div className="absolute top-4 right-4">
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={handleClose}
              disabled={isLoading}
            >
              <span className="sr-only">{t('common.close')}</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-primary-100 mr-3">
              <Sparkles className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {t('contentCreation.modals.regenerateImage.title', 'Regenerate Image')}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('contentCreation.modals.regenerateImage.description', 'Choose your preferred image style and size to generate a new image for your content.')}
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-6">
            {/* Image Style Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('contentCreation.form.imageStyle', 'Image Style')}
              </label>
              <ImageStyleSelect
                value={imageStyle}
                onChange={setImageStyle}
              />
            </div>

            {/* Image Size Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('contentCreation.form.imageSize', 'Image Size')}
              </label>
              <ImageSizeSelect
                value={imageSize}
                onChange={setImageSize}
              />
            </div>
          </div>

          {/* Generated Image Preview */}
          {generatedImageUrl && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-1">
                {t('contentCreation.modals.regenerateImage.preview', 'Generated Image Preview')}
              </h4>
              <p className="text-xs text-gray-500 mb-3">
                {t('contentCreation.modals.regenerateImage.hoverForFullSize', 'Hover for full size')}
              </p>
              <div 
                className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 cursor-zoom-in relative"
                onMouseEnter={() => setShowFullSizePreview(true)}
                onMouseLeave={() => setShowFullSizePreview(false)}
              >
                <img 
                  src={generatedImageUrl} 
                  alt={t('contentCreation.modals.regenerateImage.previewAlt', 'Generated image preview')} 
                  className="w-full h-auto object-contain max-h-64"
                />
              </div>
            </div>
          )}

          {/* Zoom x2 Preview Overlay */}
          {showFullSizePreview && generatedImageUrl && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-75 pointer-events-none transition-opacity duration-300 ease-in-out">
              <div className="relative">
                <img 
                  src={generatedImageUrl} 
                  alt={t('contentCreation.modals.regenerateImage.fullSizePreview', 'Zoom x2 preview')} 
                  className="rounded-lg shadow-2xl transition-transform duration-300 ease-in-out"
                  style={{ 
                    maxHeight: '32rem', // 2x de 16rem (max-h-64)
                    maxWidth: '90vw',
                    objectFit: 'contain'
                  }}
                />
              </div>
              <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm transition-opacity duration-300 ease-in-out">
                {t('contentCreation.modals.regenerateImage.fullSizeHint', 'Zoom x2')}
              </div>
            </div>
          )}

          <div className="mt-6 sm:flex sm:flex-row-reverse">
            {generatedImageUrl ? (
              // Show validation buttons when image is generated
              <>
                <button
                  type="button"
                  onClick={() => onValidate(generatedImageUrl)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('contentCreation.modals.regenerateImage.validate', 'Use This Image')}
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-primary-600 shadow-sm px-4 py-2 bg-white text-base font-medium text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-t-transparent border-primary-600 rounded-full animate-spin"></div>
                      {t('contentCreation.modals.regenerateImage.generating', 'Generating...')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t('contentCreation.modals.regenerateImage.regenerate', 'Generate Another')}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </>
            ) : (
              // Show initial generation button
              <>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                      {t('contentCreation.modals.regenerateImage.generating', 'Generating...')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t('contentCreation.modals.regenerateImage.generate', 'Generate New Image')}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </>
            )}
          </div>
        </div>
    </div>
  );
}; 